class FjHeroDescription extends HTMLElement {
    static FADE_MS = 300;
    static STAGGER_MS = 60;
    static STAGGER_MAX_MS = 400;
    static SLIDE_MS = 600;
    static SLIDE_EASING = 'cubic-bezier(0.45, 0, 0.2, 1)';
    static FADE_OUT_MS = 150;
    static STAGGER_OUT_MS = 20;
    static STAGGER_OUT_MAX_MS = 150;
    static SLIDE_OUT_MS = 350;

  connectedCallback() {
    this.text = this.querySelector('[data-fj-description-text]');
    this.intro = this.querySelector('[data-fj-description-intro]');
    this.ellipsis = this.querySelector('[data-fj-description-ellipsis]');
    this.more = this.querySelector('[data-fj-description-more]');
    this.separator = this.querySelector('[data-fj-description-separator]');
    this.continuation = this.querySelector('[data-fj-description-continuation]');
    this.less = this.querySelector('[data-fj-description-less]');
    const parts = [this.text, this.intro, this.ellipsis, this.more, this.separator, this.continuation, this.less];
    if (!parts.every(Boolean)) return;

    this.content = this.closest('[data-fj-hero-content]') || this;
    this.media = this.closest('[data-fj-hero-media]');
    this.fullText ??= this.intro.textContent.replace(/\s*\n\s*\n\s*/g, '\n\n').trim();
    this.rest = '';
    this.lines = [];
    this.busy = false;
    this.expanded = false;
    this.isTruncated = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleExpand = this.expand.bind(this);
    this.handleCollapse = this.collapse.bind(this);
    this.more.addEventListener('click', this.handleExpand);
    this.less.addEventListener('click', this.handleCollapse);

    this.truncate();
    this.lastWidth = this.text.clientWidth;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(this.text);
    document.fonts?.ready.then(() => this.refit());
  }

  disconnectedCallback() {
    this.more?.removeEventListener('click', this.handleExpand);
    this.less?.removeEventListener('click', this.handleCollapse);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.resizeFrame);
  }

  handleResize() {
    const width = this.text.clientWidth;
    if (width === this.lastWidth) return;
    this.lastWidth = width;
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.refit());
  }

  refit() {
    if (this.expanded || this.busy || !this.isConnected) return;
    this.truncate();
  }

  // Finds the longest intro that keeps "... Read more" within the line limit set in CSS (decimals allowed, e.g. 2.5)
  truncate() {
    const lines = parseFloat(getComputedStyle(this).getPropertyValue('--fj-hero-description-lines')) || 2;
    const lineHeight = parseFloat(getComputedStyle(this.text).lineHeight);
    const fullLines = Math.floor(lines);
    const lastLineFraction = lines - fullLines;
    const maxHeight = lineHeight * Math.ceil(lines) + 1;

    const fits = () => {
      const textRect = this.text.getBoundingClientRect();
      if (textRect.height > maxHeight) return false;
      if (!lastLineFraction || textRect.height <= lineHeight * fullLines + 1) return true;
      const endRect = this.more.hidden
        ? [...this.intro.getClientRects()].at(-1)
        : this.more.getBoundingClientRect();
      return endRect.right <= textRect.left + textRect.width * lastLineFraction;
    };

    this.intro.textContent = this.fullText;
    this.continuation.textContent = '';
    this.separator.textContent = '';
    this.rest = '';
    this.ellipsis.hidden = true;
    this.more.hidden = true;
    this.isTruncated = !fits();
    if (!this.isTruncated) return;

    this.ellipsis.hidden = false;
    this.more.hidden = false;
    const wordEnds = Array.from(this.fullText.matchAll(/[^ \t\r\n]+/g), (match) => match.index + match[0].length);
    let low = 0;
    let high = wordEnds.length - 1;
    let best = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      this.intro.textContent = this.fullText.slice(0, wordEnds[mid]);
      if (fits()) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    this.splitAt(wordEnds[best]);
  }

  splitAt(index) {
    let intro = this.fullText.slice(0, index);
    let punctuation = '';
    if (/[.,;:]$/.test(intro)) {
      punctuation = intro.slice(-1);
      intro = intro.slice(0, -1);
    }
    this.intro.textContent = intro;
    this.separator.textContent = `${punctuation} `;
    this.rest = this.fullText.slice(index);
    this.continuation.textContent = this.rest;
  }

  async expand() {
    if (this.busy || !this.isTruncated) return;
    this.busy = true;

    const contentBefore = this.content.offsetHeight;
    const mediaBefore = this.media?.offsetHeight;
    this.setExpanded(true);
    this.buildLines();
    const shift = this.content.offsetHeight - contentBefore;
    const mediaAfter = this.media?.offsetHeight;

    const slide = this.slide(shift, 0);
    const resize = this.resize(mediaBefore, mediaAfter);
    await Promise.all([slide?.finished, resize?.finished, this.fadeLines(true)]);
    slide?.cancel();
    resize?.cancel();

    this.continuation.focus({ preventScroll: true });
    this.busy = false;
  }

  async collapse() {
    if (this.busy) return;
    this.busy = true;

    const contentExpanded = this.content.offsetHeight;
    const mediaExpanded = this.media?.offsetHeight;
    this.toggleText(false);
    this.truncate(); // re-fit in case the width changed while open
    const shift = contentExpanded - this.content.offsetHeight;
    const mediaCollapsed = this.media?.offsetHeight;

    if (!this.isTruncated) {
      this.setExpanded(false);
      this.busy = false;
      return;
    }

    this.toggleText(true);
    this.buildLines();
    this.lines.forEach((line) => { line.style.opacity = '1'; });

    const slide = this.slide(0, shift);
    const resize = this.resize(mediaExpanded, mediaCollapsed);
    await Promise.all([slide?.finished, resize?.finished, this.fadeLines(false)]);
    this.setExpanded(false);
    slide?.cancel();
    resize?.cancel();

    await this.fadeInMore();
    this.more.focus({ preventScroll: true });
    this.busy = false;
  }

  toggleText(expanded) {
    this.expanded = expanded;
    this.classList.toggle('fj-hero-description--expanded', expanded);
    this.continuation.hidden = !expanded;
    this.less.hidden = !expanded;
    this.more.setAttribute('aria-expanded', String(expanded));
  }

  setExpanded(expanded) {
    this.toggleText(expanded);
    this.media?.classList.toggle('fj-collection-hero__media--expanded', expanded);
  }

  // Groups words by their rendered top position so each visual line can fade separately
  buildLines() {
    const textNode = document.createTextNode(this.rest);
    this.continuation.replaceChildren(textNode);

    const range = document.createRange();
    const groups = [];
    for (const match of this.rest.matchAll(/\S+/g)) {
      range.setStart(textNode, match.index);
      range.setEnd(textNode, match.index + match[0].length);
      const { top } = range.getBoundingClientRect();
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || Math.abs(lastGroup.top - top) > 2) {
        groups.push({ top, start: match.index });
      }
    }

    this.lines = groups.map((group, index) => {
      const line = document.createElement('span');
      line.className = 'fj-hero-description__line';
      const start = index === 0 ? 0 : group.start;
      const end = groups[index + 1]?.start ?? this.rest.length;
      line.textContent = this.rest.slice(start, end);
      return line;
    });

    this.lines.at(-1)?.append(this.less);
    this.continuation.replaceChildren(...this.lines);
  }

  async fadeLines(show) {
    const from = show ? 0 : 1;
    const to = show ? 1 : 0;

    if (!this.reducedMotion.matches) {
      const last = this.lines.length - 1;
      const stagger = Math.min(FjHeroDescription.STAGGER_MS, FjHeroDescription.STAGGER_MAX_MS / Math.max(last, 1));
      const animations = this.lines.map((line, index) => line.animate(
        [{ opacity: from }, { opacity: to }],
        {
          duration: FjHeroDescription.FADE_MS,
          delay: (show ? index : last - index) * stagger,
          easing: 'ease-out',
          fill: 'forwards',
        }
      ));
      await Promise.all(animations.map((animation) => animation.finished));
      this.lines.forEach((line) => { line.style.opacity = String(to); });
      animations.forEach((animation) => animation.cancel());
      return;
    }

    this.lines.forEach((line) => { line.style.opacity = String(to); });
  }

  slide(from, to, duration = FjHeroDescription.SLIDE_MS) {
      if (this.reducedMotion.matches || from === to) return null;
      return this.content.animate(
      [{ transform: `translateY(${from}px)` }, { transform: `translateY(${to}px)` }],
      { duration, easing: FjHeroDescription.SLIDE_EASING, fill: 'forwards' }
      );
  }

  resize(from, to) {
    if (!this.media || this.reducedMotion.matches || from === to) return null;
    return this.media.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      { duration: FjHeroDescription.SLIDE_MS, easing: FjHeroDescription.SLIDE_EASING, fill: 'forwards' }
    );
  }

  async fadeInMore() {
    if (this.reducedMotion.matches) return;
    await this.more.animate(
      [{ opacity: 0 }, { opacity: 1 }],
    { duration: FjHeroDescription.FADE_OUT_MS, easing: 'ease-out' }
    ).finished;
  }
}

if (!customElements.get('fj-hero-description')) {
  customElements.define('fj-hero-description', FjHeroDescription);
}